import { setChatVisible } from 'actions/ui/ui';
import { VisuallyHidden } from 'components/ui/VisuallyHidden/VisuallyHidden';
import { IsProduction } from 'containers/IsProduction/IsProduction';
import tooltipIcon from 'icons/tooltip-information.svg';
import type React from 'react';
import { useRef, useState } from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { RootState } from 'reducers/types';
import { chatEnabled } from 'selectors/ui/ui';
import { useTranslations } from 'utils/translations/useTranslations';

import { Ada } from './Ada/Ada';

import './Chat.scss';
import { Keys } from 'constants/keys';
import { createMarkup } from 'utils/createMarkup/createMarkup';

type PropsFromRedux = ConnectedProps<typeof connector>;

const ChatComponent = ({ enabled = true, setChatVisible }: PropsFromRedux) => {
  const i18n = useTranslations();
  const chatRef = useRef<HTMLDivElement | null>(null);
  const [showTooltipButton, setShowTooltipButton] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  function handleOnLoad() {
    setChatVisible();
    setShowTooltipButton(true);
  }

  function handleOnShow() {
    if (chatRef.current !== null) {
      chatRef.current.innerHTML = i18n.t('iframe.disclaimer');
    }
  }

  const openTooltip = () => {
    setShowTooltip(true);
  };

  const closeTooltip = () => {
    setShowTooltip(false);
  };

  const handleOnKeyDown = (evt: React.KeyboardEvent) => {
    if (evt.key === Keys.ESC) {
      closeTooltip();
    }
  };

  if (!enabled) {
    return null;
  }

  return (
    <IsProduction>
      <VisuallyHidden aria-live="polite" forwardRef={chatRef} />
      <div data-testid="chat">
        {showTooltipButton ? (
          <button
            onMouseOver={openTooltip}
            onMouseLeave={closeTooltip}
            onFocus={openTooltip}
            onBlur={closeTooltip}
            onKeyDown={handleOnKeyDown}
            tabIndex={0}
            className={'Chat-tooltipInfo buttonLikeLink'}
            aria-label={i18n.t('chat.tooltip.ariaLabel') as string}
            data-testid="chat-tooltip-button"
          >
            <img src={tooltipIcon} alt="" />
            {showTooltip ? (
              <div
                className={'Chat-tooltip'}
                data-testid="tooltip"
                aria-hidden={false}
                role="tooltip"
                dangerouslySetInnerHTML={createMarkup(
                  i18n.t('chat.tooltip.message'),
                )}
              />
            ) : null}
          </button>
        ) : null}

        <Ada onLoad={handleOnLoad} onShow={handleOnShow} />
      </div>
    </IsProduction>
  );
};

const mapStateToProps = (state: RootState) => ({
  enabled: chatEnabled(state),
});

const mapDispatchToProps = {
  setChatVisible,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const Chat = connector(ChatComponent);

export { Chat };
